import { readAnalyticsOptOut, shouldEnableAnalytics } from "@mountayaapp/analytics";

import { env } from "../../env";

/**
 * cached holds the resolved answer for the life of the page. The gate reads the
 * pathname, and startAnalytics evaluates it at bootstrap ahead of the first
 * render, so caching is what makes every later caller agree with the decision the
 * SDK itself was started on rather than re-deriving it against a pathname the
 * router has since changed.
 */
let cached: boolean | undefined;

/**
 * isSessionReplayEnabled reports whether the session recorder is running on this
 * page load. The application passes `replay: true` whenever it initializes
 * analytics at all, so the analytics enablement gate is exactly the replay gate:
 * a build without a project token, the Playwright suite, the Vitest run, the
 * embed surface, and a device belonging to an excluded person all answer false.
 *
 * It exists because session replay reaches past the DOM. rrweb snapshots a WebGL
 * canvas by reading its drawing buffer back out-of-band, which only returns the
 * rendered frame when the context was constructed with preserveDrawingBuffer —
 * and MapLibre reads that attribute once, at construction. So the map surfaces
 * have to know, before they build their context, whether anything is going to
 * read it. Answering here rather than passing `true` unconditionally is what
 * keeps the per-frame cost of preserving the buffer off every surface no
 * recorder is watching.
 *
 * A mid-session opt-out does not retroactively flip this. It does not need to:
 * optOutAnalytics stops the recorder outright, so nothing reads the canvas from
 * that moment on, and the map keeps preserving a buffer no one samples until the
 * next page load.
 */
export const isSessionReplayEnabled = (): boolean => {
  cached ??= shouldEnableAnalytics({
    isE2E: import.meta.env.VITE_E2E === "true",
    isOptedOut: readAnalyticsOptOut(),
    mode: import.meta.env.MODE,
    pathname: window.location.pathname,
    token: env.posthogProjectToken,
  });

  return cached;
};
